import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ModernProductSelectorV2 from "./ModernProductSelectorV2";
import { PMDialog } from "../PMDialog";

function normalizeFilters(filters) {
  if (!Array.isArray(filters)) return [];
  return filters.map((f) => {
    if (!f || typeof f !== "object") return f;
    if (f.name != null) return f;
    if (f.key != null) return { name: String(f.key), value: f.value };
    return f;
  });
}

function normalizeSelectedItems(selectedItems) {
  const arr = Array.isArray(selectedItems) ? selectedItems : [];
  return arr
    .map((item) => {
      if (item == null) return null;
      if (typeof item === "object" && !Array.isArray(item) && item.id != null) {
        const id = parseInt(item.id, 10);
        if (isNaN(id)) return null;
        return { ...item, id };
      }
      const id = parseInt(item, 10);
      return isNaN(id) ? null : { id };
    })
    .filter(Boolean);
}

/**
 * Legacy-compatible product picker: same props as the old ItemSelectorV2, UI from ModernProductSelectorV2.
 * - Single selection: modern flow (confirm on pick, PMDialog from Modern).
 * - Multiple selection: draft selection + Lagre / Avbryt (matches old "Lagre" behaviour).
 */
export default function ItemSelectorV2({
  multiple = true,
  selectedItems = [],
  onChange,
  onClose,
  open = false,
  /** When true (default), uses dense PMDialog layout aligned with item-consumption-list / ProductSelectorDialog */
  compact = true,
  filters = [],
  autoClose: _autoClose,
  showQuantityInput = false,
  showWarehouseOnlyDefault = false,
  /** Default false: outer dialog already shows the title; avoids duplicate “Velg produkt(er)” header */
  showHeader = false,
  hideCategoryAndSystemNav = false,
  defaultShowActiveOnly,
  defaultShowExpired,
  searchPlaceholder,
  searchOnly = false,
  defaultFilterSystemsOnly = false,
  ...rest
}) {
  void _autoClose;
  void rest;

  const { t } = useTranslation();
  const pmdialogTitleKey = multiple
    ? "inventory.count.select_products"
    : "inventory.count.select_product";

  const normalizedFilters = useMemo(() => normalizeFilters(filters), [filters]);
  const initialSelection = useMemo(
    () => normalizeSelectedItems(selectedItems),
    [selectedItems]
  );

  const [draftSelection, setDraftSelection] = useState(initialSelection);

  useEffect(() => {
    if (!open) return;
    setDraftSelection(normalizeSelectedItems(selectedItems));
  }, [open, selectedItems]);

  const handleSaveMultiple = useCallback(() => {
    if (onChange) onChange(draftSelection);
    if (onClose) onClose();
  }, [draftSelection, onChange, onClose]);

  const handleCancelMultiple = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  if (!open) return null;

  const dialogSize = compact ? "4xl" : "7xl";

  if (multiple) {
    return (
      <PMDialog
        open={open}
        onClose={handleCancelMultiple}
        title={pmdialogTitleKey}
        size={dialogSize}
        closeOnOutsideClick={true}
        zIndex={3000}
        dense={compact}
      >
        <div className="flex flex-col min-h-0 flex-1 w-full overflow-hidden">
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <ModernProductSelectorV2
              embeddedInParentDialog
              open={open}
              multiple
              selectedProducts={draftSelection}
              onChange={setDraftSelection}
              onClose={handleCancelMultiple}
              filters={normalizedFilters}
              showHeader={showHeader}
              showWarehouseOnlyDefault={showWarehouseOnlyDefault}
              showQuantityInput={showQuantityInput}
              hideCategoryAndSystemNav={hideCategoryAndSystemNav}
              defaultShowActiveOnly={defaultShowActiveOnly}
              defaultShowExpired={defaultShowExpired}
              searchPlaceholder={searchPlaceholder}
              searchOnly={searchOnly}
              defaultFilterSystemsOnly={defaultFilterSystemsOnly}
            />
          </div>
          <div className="flex-shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50/80 sm:bg-transparent sm:px-0">
            <button
              type="button"
              onClick={handleCancelMultiple}
              className="w-full sm:w-auto px-4 py-2.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm font-medium"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={handleSaveMultiple}
              className="w-full sm:w-auto px-4 py-2.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium shadow-sm"
            >
              {t("Save")}
            </button>
          </div>
        </div>
      </PMDialog>
    );
  }

  return (
    <ModernProductSelectorV2
      open={open}
      multiple={false}
      selectedProducts={initialSelection}
      onChange={(products) => {
        if (onChange) onChange(products);
      }}
      onClose={() => onClose && onClose()}
      filters={normalizedFilters}
      showHeader={showHeader}
      showWarehouseOnlyDefault={showWarehouseOnlyDefault}
      showQuantityInput={showQuantityInput}
      embeddedInParentDialog={false}
      pmdialogTitle={pmdialogTitleKey}
      pmdialogSize={dialogSize}
      pmdialogDense={compact}
      hideCategoryAndSystemNav={hideCategoryAndSystemNav}
      defaultShowActiveOnly={defaultShowActiveOnly}
      defaultShowExpired={defaultShowExpired}
      searchPlaceholder={searchPlaceholder}
      searchOnly={searchOnly}
      defaultFilterSystemsOnly={defaultFilterSystemsOnly}
    />
  );
}
